<template>
    <!-- ORDER MODIFICATIONS SECTION -->
    <div class="mt-2 md:mt-6">
        <h2 class="font-bold uppercase tracking-wider">
            {{ $t("orderModifications.orderModifications") }}
        </h2>
        <table class="w-full divide-y divide-gray-200 mt-2 md:mt-4">
            <thead class="bg-minplan-lightgreybeige">
                <tr>
                    <th
                        scope="col"
                        class="px-1 lg:px-3 py-2 lg:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider"
                    >
                        {{ $t("orderModifications.title") }}
                    </th>
                    <th
                        scope="col"
                        class="px-1 lg:px-3 py-2 lg:py-3 text-right text-xs font-medium text-minplan-beige uppercase tracking-wider"
                    >
                        {{ $t(costLabelKey) }}
                    </th>
                    <!-- Entscheid 12 (Auftrag 2026-08-16): die Spalten-Verwaltung
                         der Abrechnung wirkt neu AUCH hier — aber nur mit der
                         Spalte "Budget". "Kosten" bleibt immer stehen, sonst
                         hätte die Tabelle keinen Zweck. -->
                    <th
                        v-if="showBudget"
                        scope="col"
                        class="px-1 lg:px-3 py-2 lg:py-3 text-right text-xs font-medium text-minplan-beige uppercase tracking-wider"
                    >
                        {{ $t("orderModifications.budget") }}
                    </th>
                    <!-- Entscheid 11: im Offerten-Modus entfällt "Differenz" —
                         dort hängen Budget und Gebühr nicht mehr zusammen. Im
                         Budget-Modus bleibt sie, denn genau daraus entsteht
                         die Gebühr. -->
                    <th
                        v-if="isBudgetBased"
                        scope="col"
                        class="px-1 lg:px-3 py-2 lg:py-3 text-right text-xs font-medium text-minplan-beige uppercase tracking-wider"
                    >
                        {{ $t("orderModifications.difference") }}
                    </th>
                    <th
                        scope="col"
                        class="px-1 lg:px-3 py-2 lg:py-3 text-right text-xs font-medium text-minplan-beige uppercase tracking-wider"
                    >
                        {{ $t("orderModifications.state") }}
                    </th>
                    <th></th>
                </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
                <tr v-if="orderModifications.length === 0">
                    <td
                        :colspan="columnCount"
                        class="p-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        {{ $t("orderModifications.emptyOrderModifications") }}
                    </td>
                </tr>
                <tr v-for="mod in orderModifications" :key="mod.uuid">
                    <td
                        class="px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        {{ mod.title }}
                    </td>
                    <td
                        class="text-right px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        {{ displayCost(mod) }}
                    </td>
                    <td
                        v-if="showBudget"
                        class="text-right px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        {{ mod.budget }}
                    </td>
                    <td
                        v-if="isBudgetBased"
                        class="text-right px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        {{ mod.diff }}
                    </td>
                    <td
                        class="text-right px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        <span
                            :class="mod.style"
                            class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                        >
                            {{ $t("orderModifications.states." + mod.state) }}
                        </span>
                    </td>
                    <td
                        class="text-right px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                    >
                        <button
                            @click="toModification(mod.uuid)"
                            class="cursor-pointer inline-flex items-center space-x-2 bg-gray-100 rounded px-2 py-1 hover:bg-gray-200 hover:text-gray-700 transition duration-200"
                        >
                            <span>{{ $t("orderModifications.showDetails") }}</span>
                            <SearchIcon
                                class="h-5 w-5 flex-shrink-0 text-gray-500 group-hover:text-gray-700 transition duration-200"
                                aria-hidden="true"
                            />
                        </button>
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <td :colspan="columnCount" class="text-right text-sm text-gray-500">
                    {{ $t("orderModifications.vatInfo") }}
                </td>
            </tfoot>
        </table>
    </div>
    <!-- CREATE ORDER MODIFICATION BUTTON -->
    <div class="flex justify-end mt-6 mb-3">
        <button
            type="button"
            v-if="createModificationRoute"
            @click="openCreateModal"
            class="ml-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-minplan-greybeige hover:bg-minplan-lightgreybeige focus:outline-none uppercase"
        >
            <PlusIcon class="-ml-1 mr-2 h-5 w-5 text-minplan-darkgreybeige" />
            <span class="inline font-bold">{{ $t("orderModifications.new") }}</span>
        </button>
    </div>
</template>

<script>
import JetDialogModal from "@/Jetstream/DialogModal";
import { PlusIcon, SearchIcon } from "@heroicons/vue/outline";
import { defineComponent } from "vue";

// `defineComponent()` statt des blanken `export default {}`: dieselbe Form wie
// in `ApartmentBudgetTable.vue`, weil der Vitest-Fall dieser Datei die Optionen
// direkt importiert und der Wrapper ihm (und der IDE) den Komponententyp gibt.
// Verhaltensgleich — er reicht sein Argument unveraendert durch.
export default defineComponent({
    props: {
        topic: { type: Object, required: true },
        orderModifications: { type: Array, required: true },
        createModificationRoute: { type: String, default: null },
        showModificationRoute: { type: String, required: true },
        // Auftrag 2026-08-16, Entscheid 12: die Spalte "Budget" folgt der
        // Spalten-Verwaltung der Abrechnung — je Zielgruppe. Fehlt die Prop
        // (Default), bleibt die Tabelle wie bisher vollständig; damit ist der
        // Bestand ohne Modus-Angabe unverändert.
        showBudget: { type: Boolean, default: true },
        // Entscheid 11: "Differenz" gibt es nur im Budget-Modus.
        isBudgetBased: { type: Boolean, default: false },
    },
    data() {
        return {
            showModal: false,
            currentComment: null,
        };
    },
    components: {
        JetDialogModal,
        PlusIcon,
        SearchIcon,
    },
    computed: {
        // Auftrag Luca, 2026-08-25, Entscheide 1.1 und 1.2: dieselbe Spalte
        // heisst im budgetbasierten Modus «Offeriert».
        costLabelKey() {
            return this.isBudgetBased
                ? "orderModifications.quoted"
                : "orderModifications.cost";
        },
        // Bezeichnung, Kosten, Status und die Aktionsspalte stehen immer; die
        // beiden mittleren kommen dazu. Vorher waren die `colspan` der
        // Leerzeile (7) und der Fusszeile (9) fest verdrahtet und schon bei
        // sechs Spalten falsch — beim Ausblenden wäre das sichtbar geworden.
        columnCount() {
            return 4 + (this.showBudget ? 1 : 0) + (this.isBudgetBased ? 1 : 0);
        },
    },
    methods: {
        // Thema 2b (Auftrag Luca, 23.09.2026): wo der Server `costWithFees`
        // mitliefert, nennt die Spalte «Kosten» den Betrag, den die Aenderung
        // den Kaeufer WIRKLICH kostet — Gebuehr und Honorar eingerechnet.
        // Genau hier stand der Befund: die Liste zeigte 20'214.70, die Offerte
        // daneben 21'225.45. `cost` bleibt unveraendert daneben stehen; die
        // Listenseiten ohne den Opt-in zeigen weiterhin die blanke
        // Offertensumme.
        displayCost(mod) {
            return mod.costWithFees ?? mod.cost;
        },
        openCreateModal() {
            this.$inertia.visitInModal(
                this.route(this.createModificationRoute, this.topic.uuid)
            );
        },
        toModification(uuid) {
            this.$inertia.get(
                this.route(this.showModificationRoute, {
                    topic: this.topic.uuid,
                    orderModification: uuid,
                })
            );
        },
    },
});
</script>

<style scoped></style>
